<script setup lang="ts">
import { computed } from 'vue'
import { useHelpCenter } from '@/composables/useHelpCenter'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import {
  X,
  Rocket,
  LayoutDashboard,
  Building2,
  FolderKanban,
  ListTodo,
  ClipboardList,
  Layers,
  MessageSquarePlus,
  Ticket,
  PackageCheck,
  Users,
  Calendar,
  Video,
  FileText,
  Receipt,
  ShoppingCart,
  UserCog,
  Settings,
  Bot,
  Shield,
  Briefcase,
  Handshake,
  UsersRound,
  Wrench,
  ChevronRight,
} from 'lucide-vue-next'

import type { HelpModule, ModuleGroupConfig } from '@/types/help'

const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  close: []
  selectModule: [moduleId: HelpModule]
}>()

const { availableModules, navigateToModule } = useHelpCenter()

// Configuración de grupos de módulos
const moduleGroups: ModuleGroupConfig[] = [
  {
    id: 'inicio',
    title: 'Inicio',
    icon: 'Rocket',
    modules: ['getting-started', 'dashboard'],
  },
  {
    id: 'proyectos',
    title: 'Gestión de Proyectos',
    icon: 'FolderKanban',
    modules: ['projects', 'backlog', 'requirements', 'epics', 'releases'],
  },
  {
    id: 'crm',
    title: 'CRM y Ventas',
    icon: 'Handshake',
    modules: ['crm', 'organizations', 'quotations', 'sales-orders'],
  },
  {
    id: 'colaboracion',
    title: 'Colaboración',
    icon: 'UsersRound',
    modules: ['calendar', 'meetings', 'documents'],
  },
  {
    id: 'soporte',
    title: 'Soporte al Cliente',
    icon: 'Ticket',
    modules: ['tickets', 'petitions'],
  },
  {
    id: 'configuracion',
    title: 'Configuración',
    icon: 'Wrench',
    modules: ['profile', 'admin', 'copilot', 'super-admin'],
  },
]

// Mapa de iconos
const iconMap: Record<string, unknown> = {
  Rocket,
  LayoutDashboard,
  Building2,
  FolderKanban,
  ListTodo,
  ClipboardList,
  Layers,
  MessageSquarePlus,
  Ticket,
  PackageCheck,
  Users,
  Calendar,
  Video,
  FileText,
  Receipt,
  ShoppingCart,
  UserCog,
  Settings,
  Bot,
  Shield,
  Briefcase,
  Handshake,
  UsersRound,
  Wrench,
}

const getIcon = (iconName: string) => {
  return iconMap[iconName] || FolderKanban
}

// Filtrar grupos que tienen módulos disponibles para el usuario
const filteredGroups = computed(() => {
  const availableIds = new Set(availableModules.value.map((m) => m.id))

  return moduleGroups
    .map((group) => ({
      ...group,
      availableModules: group.modules
        .filter((modId) => availableIds.has(modId))
        .map((modId) => availableModules.value.find((m) => m.id === modId)!)
        .filter(Boolean),
    }))
    .filter((group) => group.availableModules.length > 0)
})

const handleSelectModule = (moduleId: HelpModule) => {
  navigateToModule(moduleId)
  emit('selectModule', moduleId)
  emit('close')
}
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-300 ease-out"
    leave-active-class="transition-all duration-200 ease-in"
    enter-from-class="translate-x-full opacity-0"
    enter-to-class="translate-x-0 opacity-100"
    leave-from-class="translate-x-0 opacity-100"
    leave-to-class="translate-x-full opacity-0"
  >
    <div
      v-if="isOpen"
      class="fixed top-0 right-0 h-full w-80 bg-background border-l shadow-xl z-[110] flex flex-col"
    >
      <!-- Header -->
      <header class="h-14 border-b flex items-center justify-between px-4 shrink-0">
        <h2 class="font-semibold">Explorar Guías</h2>
        <Button variant="ghost" size="icon" @click="$emit('close')">
          <X class="h-4 w-4" />
        </Button>
      </header>

      <!-- Content -->
      <ScrollArea class="flex-1">
        <div class="p-4 space-y-6">
          <div v-for="group in filteredGroups" :key="group.id">
            <!-- Group Header -->
            <div class="flex items-center gap-2 mb-3">
              <div class="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                <component :is="getIcon(group.icon)" class="h-4 w-4 text-primary" />
              </div>
              <h3 class="font-semibold text-sm">{{ group.title }}</h3>
              <Badge variant="secondary" class="ml-auto text-xs">
                {{ group.availableModules.length }}
              </Badge>
            </div>

            <!-- Module List -->
            <div class="space-y-1 ml-2">
              <button
                v-for="mod in group.availableModules"
                :key="mod.id"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted text-left transition-colors group"
                @click="handleSelectModule(mod.id)"
              >
                <component
                  :is="getIcon(mod.icon)"
                  class="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors"
                />
                <span class="flex-1 text-sm">{{ mod.title }}</span>
                <ChevronRight class="h-4 w-4 text-muted-foreground/50 group-hover:text-primary transition-colors" />
              </button>
            </div>
          </div>
        </div>
      </ScrollArea>

      <!-- Footer -->
      <div class="p-4 border-t">
        <p class="text-xs text-muted-foreground text-center">
          {{ availableModules.length }} módulos disponibles
        </p>
      </div>
    </div>
  </Transition>

  <!-- Overlay -->
  <Transition
    enter-active-class="transition-opacity duration-300"
    leave-active-class="transition-opacity duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      class="fixed inset-0 bg-black/20 z-[105]"
      @click="$emit('close')"
    />
  </Transition>
</template>
